import { Bounds } from "../../../engine/space/bounds";
import { RNG } from "rot-js";
import buildMinSpanTree, { GraphNode } from "./buildMinSpanTree";

export const BSPGenerate = (rng: typeof RNG, width: number, height: number) => {
  const graph = graphRooms(rng, makeInitialRooms(rng, 8, width, height));
  const mstgraph = buildMinSpanTree(graph, 0);
  raiseLeafs(mstgraph);
  addLoops(rng, 0.45, graph, mstgraph);
  pruneLeafs(rng, 1, mstgraph);
  return mstgraph;
};

const makeInitialRooms = (
  rng: typeof RNG,
  maxDepth: number,
  width: number,
  height: number
) => {
  const result: Bounds[] = [];
  const container = { x: 0, y: 0, w: width, h: height };
  const queue: [Bounds, number][] = [[container, 0]];

  let next: [Bounds, number] | undefined, area, depth, l, r;
  // eslint-disable-next-line no-cond-assign
  while ((next = queue.pop())) {
    [area, depth] = next;
    if (depth <= maxDepth && (8 <= area.w || 8 <= area.h)) {
      [l, r] = splitArea(rng, area);
      queue.push([l, depth + 1], [r, depth + 1]);
    } else {
      result.push(area);
    }
  }
  return result;
};

const splitArea = (rng: typeof RNG, bounds: Bounds) => {
  let l, r;
  if (8 <= bounds.w && 0.45 < bounds.w / bounds.h) {
    const min = Math.floor(bounds.w * 0.45);
    const max = Math.ceil(bounds.w * 0.55);
    l = {
      x: bounds.x,
      y: bounds.y,
      w: rng.getUniformInt(min, max),
      h: bounds.h,
    };
    r = { x: bounds.x + l.w, y: bounds.y, w: bounds.w - l.w, h: bounds.h };
  } else {
    const min = Math.floor(bounds.h * 0.45);
    const max = Math.ceil(bounds.h * 0.55);
    l = {
      x: bounds.x,
      y: bounds.y,
      w: bounds.w,
      h: rng.getUniformInt(min, max),
    };
    r = { x: bounds.x, y: bounds.y + l.h, w: bounds.w, h: bounds.h - l.h };
  }
  return [l, r] as const;
};

const graphRooms = (rng: typeof RNG, rooms: Bounds[]) => {
  const result: Map<number, GraphNode> = new Map();

  let iRoom, jRoom, iResult, jResult;
  for (let i = 0; i < rooms.length; i++) {
    iRoom = rooms[i];
    iResult =
      result.get(i) ||
      result
        .set(i, { bounds: iRoom, adj: [], weight: rng.getUniform(), z: 1 })
        .get(i);
    for (let j = i + 1; j < rooms.length; j++) {
      jRoom = rooms[j];
      if (compareRooms(iRoom, jRoom)) {
        jResult =
          result.get(j) ||
          result
            .set(j, { bounds: jRoom, adj: [], weight: rng.getUniform(), z: 1 })
            .get(j);
        iResult?.adj.push(j);
        jResult?.adj.push(i);
      }
    }
  }
  return result;
};

const compareRooms = (a: Bounds, b: Bounds) => {
  const xTouch = a.x <= b.x + b.w && b.x <= a.x + a.w;
  const yTouch = a.y <= b.y + b.h && b.y <= a.y + a.h;
  const xOverlap = a.x < b.x + b.w && b.x < a.x + a.w;
  const yOverlap = a.y < b.y + b.h && b.y < a.y + a.h;
  return (xTouch && yOverlap) || (yTouch && xOverlap);
};

const addLoops = (
  rng: typeof RNG,
  loopPct: number,
  fullGraph: ReturnType<typeof graphRooms>,
  mstGraph: ReturnType<typeof graphRooms>
) => {
  const tLoops = Math.floor(mstGraph.size * loopPct);
  for (let i = 0; i <= tLoops; i++) {
    for (let bail = 0; bail <= 100; bail++) {
      const id = rng.getUniformInt(0, mstGraph.size - 1);
      const room = mstGraph.get(id);
      if (room) {
        const fullRoom = fullGraph.get(id);
        if (fullRoom) {
          if (room.adj.length < fullRoom.adj.length) {
            const diff = fullRoom.adj.filter((num) => !room.adj.includes(num));
            const tId = rng.getItem(diff);
            if (tId != null) {
              const target = mstGraph.get(tId);
              target?.adj.push(id);
              room.adj.push(tId);
              break;
            }
          }
        } else {
          console.log("no full room found");
        }
      }
    }
  }
};

const pruneLeafs = (
  rng: typeof RNG,
  maxDepth: number,
  graph: ReturnType<typeof graphRooms>
) => {
  const queue = [];
  let room: GraphNode, i: number, depth: number;
  for ([i, room] of graph.entries()) {
    if (room.adj.length === 1) {
      queue.push([i, room, 0]);
    }
  }

  let next: [number, GraphNode, number];
  // eslint-disable-next-line no-cond-assign
  while ((next = queue.pop())) {
    [i, room, depth] = next;
    graph.delete(i);
    const adjRoom = graph.get(room.adj[0]);
    if (adjRoom) {
      adjRoom.adj = adjRoom.adj.filter((j) => j !== i);
      if (adjRoom.adj.length === 1 && depth <= maxDepth) {
        queue.push([room.adj[0], adjRoom, depth + 1]);
      }
    }
  }
};

const raiseLeafs = (graph: ReturnType<typeof graphRooms>) => {
  let room: GraphNode, i: number, depth: number;
  for ([i, room] of graph.entries()) {
    if (room.adj.length === 1) {
      room.z = 2;
    }
  }
};
